import React, { createContext, useContext, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { fetchDelivery, saveDelivery, uploadDeliveryAttachment } from '../../../../../features/deliveries/deliveryActions';
import { fetchChecklistBySlug, fetchChecklistStats } from '../../../../../features/checks/checkActions';
import { getUsersAsync } from '../../../../../features/users/usersActions';
import Toast from '../../../../../components/Toast';

const DeliveryDetailContext = createContext(null);
const draftFor = delivery => ({
  notes: delivery.notes || '', receivedById: delivery.receivedById || '', approvedById: delivery.approvedById || '',
  managerSignature: delivery.managerSignature || '', deliveryTemp: delivery.deliveryTemp ?? '',
  outcome: delivery.outcome || '', approved: delivery.approved === true,
  itemResults: delivery.itemResults.map(result => ({ checklistItemId: result.checklistItemId, status: result.status, comment: result.comment || '', issuePhotoUrl: result.issuePhotoUrl || '' })),
  bulletResults: delivery.bulletResults.map(result => ({ checklistItemId: result.checklistItemId, bulletId: result.bulletId, isChecked: result.isChecked })),
});
export function DeliveryDetailProvider({ id, children }) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const record = useSelector(state => state.deliveries.byId[id]);
  const checklist = useSelector(state => state.checks.checklistsBySlug['delivery-check']);
  const users = useSelector(state => state.users.users);
  const usersError = useSelector(state => state.users.error);
  const [draft, setDraft] = useState(null);
  const [templateLoading, setTemplateLoading] = useState(true);
  const [templateError, setTemplateError] = useState(null);
  const [operationError, setOperationError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [dirty, setDirty] = useState(false);
  const initialized = useRef(false);
  const busyRef = useRef(false);
  const delivery = record?.data;
  const readOnly = delivery?.status === 'SUBMITTED' || Boolean(delivery?.legacySubmissionId);
  useEffect(() => {
    const request = dispatch(fetchDelivery(id));
    let active = true;
    dispatch(fetchChecklistBySlug('delivery-check')).unwrap()
      .catch(error => { if (active) setTemplateError(error); })
      .finally(() => { if (active) setTemplateLoading(false); });
    dispatch(getUsersAsync());
    return () => { active = false; request.abort(); };
  }, [dispatch, id]);
  useEffect(() => {
    if (delivery && !record.loading && !initialized.current) {
      setDraft(draftFor(delivery));
      initialized.current = true;
    }
  }, [delivery, record?.loading]);
  const change = update => {
    if (busyRef.current || readOnly) return;
    setDraft(previous => update(previous));
    setDirty(true);
  };
  const updateField = (field, value) => change(previous => ({ ...previous, [field]: value }));
  const updateItem = (itemId, fields) => change(previous => {
    const current = previous.itemResults.find(result => result.checklistItemId === itemId);
    return { ...previous, itemResults: [...previous.itemResults.filter(result => result.checklistItemId !== itemId), { checklistItemId: itemId, ...current, ...fields }] };
  });
  const updateBullet = (itemId, bulletId, isChecked) => change(previous => ({
    ...previous, bulletResults: [...previous.bulletResults.filter(result => result.bulletId !== bulletId), { checklistItemId: itemId, bulletId, isChecked }],
  }));
  const selectAllBullets = itemId => {
    const item = checklist?.items.find(row => row.id === itemId);
    if (!item?.bullets.length) return;
    const ids = new Set(item.bullets.map(bullet => bullet.id));
    change(previous => ({ ...previous, bulletResults: [
      ...previous.bulletResults.filter(row => !ids.has(row.bulletId)),
      ...item.bullets.map(bullet => ({ ...previous.bulletResults.find(row => row.bulletId === bullet.id), checklistItemId: itemId, bulletId: bullet.id, isChecked: true })),
    ] }));
  };
  const save = async (signOff = false) => {
    if (busyRef.current || readOnly || !draft || !delivery || (signOff && draft.approved !== true)) return false;
    busyRef.current = true;
    setBusy(true);
    setOperationError(null);
    try {
      const response = await dispatch(saveDelivery({ id, payload: { ...draft, version: delivery.version, signOff } })).unwrap();
      setDraft(draftFor(response.data));
      setDirty(false);
      Toast('success', signOff ? 'Check Submitted' : 'Delivery draft saved');
      dispatch(fetchChecklistStats(response.data.deliveryDate));
      if (signOff) navigate('/checks/delivery-check', { replace: true });
      return true;
    } catch (error) { setOperationError(error); Toast('error', error); return false; }
    finally { busyRef.current = false; setBusy(false); }
  };
  const uploadAttachment = async (file, itemId) => {
    if (!file || busyRef.current || readOnly) return;
    busyRef.current = true;
    setBusy(true);
    setOperationError(null);
    try {
      const oldIds = new Set(delivery.attachments.map(attachment => attachment.id));
      const response = await dispatch(uploadDeliveryAttachment({ id, file })).unwrap();
      const uploaded = response.data.attachments.find(attachment => !oldIds.has(attachment.id));
      if (itemId && uploaded) {
        setDraft(previous => {
          const current = previous.itemResults.find(result => result.checklistItemId === itemId);
          return { ...previous, itemResults: [...previous.itemResults.filter(result => result.checklistItemId !== itemId), { checklistItemId: itemId, ...current, issuePhotoUrl: uploaded.photoUrl }] };
        });
        setDirty(true);
      }
      Toast('success', 'Attachment uploaded');
    } catch (error) { setOperationError(error); Toast('error', error); }
    finally { busyRef.current = false; setBusy(false); }
  };
  const reload = async () => {
    if (busyRef.current) return;
    try {
      if (templateError) {
        setTemplateLoading(true);
        await dispatch(fetchChecklistBySlug('delivery-check')).unwrap();
        setTemplateError(null);
      }
      const response = await dispatch(fetchDelivery(id)).unwrap();
      setDraft(draftFor(response.data)); setDirty(false); setOperationError(null);
      dispatch(getUsersAsync());
    } catch (error) { Toast('error', error); }
    finally { setTemplateLoading(false); }
  };
  const items = checklist?.items || [];
  const answered = draft?.itemResults.filter(result => ['PASS', 'ISSUE'].includes(result.status)).length || 0;
  const issues = draft?.itemResults.filter(result => result.status === 'ISSUE').length || 0;
  return <DeliveryDetailContext.Provider value={{
    delivery, draft, checklist, busy, dirty, readOnly,
    loading: templateLoading || !record || record.loading || (!draft && !record.error),
    error: templateError || (!draft ? record?.error : null), operationError, usersError,
    staff: users.filter(user => user.role?.name?.toUpperCase() === 'STAFF'),
    managers: users.filter(user => ['ADMIN', 'MANAGER'].includes(user.role?.name?.toUpperCase())),
    updateField, updateItem, updateBullet, selectAllBullets, save, uploadAttachment, reload,
    answered, issues, total: items.length,
    canSubmit: !busy && !readOnly && draft?.approved === true && items.length > 0 && answered === items.length && Boolean(draft?.receivedById && draft?.approvedById && draft?.outcome),
  }}>{children}</DeliveryDetailContext.Provider>;
}
export function useDeliveryDetail() {
  const context = useContext(DeliveryDetailContext);
  if (!context) throw new Error('useDeliveryDetail must be used inside DeliveryDetailProvider');
  return context;
}
